import React from 'react';

import { Box } from '@mui/material';

import { makeStyles } from 'tss-react/mui';

export type ValidColor =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'grey'
  | 'black';

export type ValidSize =
  | 'sm'
  | 'md'
  | 'ml'
  | 'lg';

const sizes: Record<ValidSize, string> = {
  sm: '4px',
  md: '6px',
  ml: '8px',
  lg: '10px'
};

type StyleProps = {
  size: ValidSize;
  color: ValidColor;
  hasChildren: boolean;
};

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  indicator: {
    backgroundColor: (() => {
      if (props.color === 'grey') {
        return theme.palette.grey[500];
      }

      if (props.color === 'black') {
        return theme.palette.common.black;
      }

      return theme.palette[props.color].main;
    })(),
    borderRadius: '50%',
    display: props.hasChildren ? 'block' : 'inline-block',
    height: sizes[props.size],
    marginRight: props.hasChildren ? theme.spacing(1) : 0,
    width: sizes[props.size],
    '&.disabled': {
      backgroundColor: theme.palette.text.disabled,
    }
  },
  wrapper: {
    color: props.color === 'error' ?  theme.palette[props.color].main : theme.palette.text.primary,
    fontSize: '14px',
    '&.disabled': {
      color: 'rgba(0, 0, 0, 0.38)'
    }
  }
}));

export type IndicatorProps = {
  size?: ValidSize;
  color?: ValidColor;
  className?: string;
  disabled?: boolean;
};

export const Indicator: React.FC<IndicatorProps> = props => {
  const { classes, cx } = useStyles({
    size: props.size ?? 'md',
    color: props.color ?? 'primary',
    hasChildren: !!props.children
  });

  const boxClassName = cx(classes.wrapper, props.disabled && 'disabled', props.className);
  const indicatorClassName = cx(classes.indicator, props.disabled && 'disabled');

  if (props.children) {
    return (
      <Box display="flex" alignItems="center" className={boxClassName}>
        <i className={indicatorClassName} />
        <div>
          {props.children}
        </div>
      </Box>
    );
  }

  return <i className={cx(indicatorClassName, props.className)} />;
};

Indicator.defaultProps = {
  size: 'md',
  color: 'primary'
};
